import { Badge } from "@/components/design-system/badge";
import ExportDropdown, {
  ExportOption,
} from "@/components/design-system/export-dropdown";
import ListHeader from "@/components/design-system/list-header";
import ListPagination from "@/components/design-system/list-pagination";
import {
  Table,
  TableColumn,
  type TableSortConfig,
} from "@/components/design-system/table";
import { downloadFeedbackService } from "@/service/feedback.service";
import { selectUserRole } from "@/store/slices/authSlice";
import { setNavigationSource } from "@/store/slices/courseSlice";
import {
  fetchFeedbacks,
  selectFeedbackLoading,
  selectFeedbacks,
  selectNoFeedbacks,
  selectTotalItems,
} from "@/store/slices/feedbackSlice";
import { DownloadFeedbackQuery, FeedbackModel } from "@/types/feedback.model";
import {
  capitalizeFirstLetter,
  CSVIcon,
  downloadFileFromBlob,
  ExcelIcon,
  FileFormats,
  hasMenuPermission,
  hasRolePermissions,
  PDFIcon,
  ROLE_PERMISSIONS,
  successToast,
} from "@/utils/constants";
import { Eye } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// Extended interface for UI display purposes
interface FeedbackDisplay extends FeedbackModel {
  sno?: number;
}

const ITEMS_PER_PAGE = 10;

/**
 * FeedbackListSection Component
 *
 * Displays a list of feedbacks in a table format with search, filter, and pagination.
 * Also provides actions to view, edit, and delete feedbacks.
 */
const FeedbackListSection: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const feedbacks = useSelector(selectFeedbacks);
  const loading = useSelector(selectFeedbackLoading);
  // const actionLoading = useSelector(selectFeedbackActionLoading);
  const totalItems = useSelector(selectTotalItems);
  const userRole = useSelector(selectUserRole);
  const noFeedbacks = useSelector(selectNoFeedbacks);

  // State for search, pagination, date range, and sorting
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<TableSortConfig | undefined>(
    undefined
  );

  // Handle sorting when column headers are clicked
  const handleSort = (config: TableSortConfig | null) => {
    setSortConfig(config || undefined);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Map frontend column keys to backend API sorting keys
  const getSortField = (key: string): string => {
    const sortFieldMap: Record<string, string> = {
      student: "STND", // Student Name
      regno: "REG", // Unique ID
      grade: "GRDE", // Grade
      lessonName: "LSN", // Lesson name
      unitName: "UNT", // Unit name
      status: "STS", // Status
      type: "TYP", // Assignment type
      date: "CDT", // Created date
      teacherName: "TCH", // Teacher name
    };
    return sortFieldMap[key] || key;
  };

  // Fetch feedbacks when component mounts or filters change
  useEffect(() => {
    // Reset to first page when search value changes
    if (searchValue && currentPage !== 1) {
      setCurrentPage(1);
      return; // The page change will trigger another fetch
    }

    // Prepare sorting parameters if sort config exists
    const sortParams = sortConfig
      ? {
          sortBy: getSortField(sortConfig.key),
        }
      : {};

    dispatch(
      fetchFeedbacks({
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        search: searchValue,
        ...sortParams,
      }) as any
    );
  }, [dispatch, currentPage, searchValue, sortConfig]);

  // Handle search input changes
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Table columns configuration
  const columns: TableColumn<FeedbackDisplay>[] = [
    {
      key: "sno",
      title: "S.No",
      dataIndex: "sno",
      minWidth: 68,
      maxWidth: 68,
      align: "center",
      render: (_, __, index) => {
        // Calculate S.No based on current page and index
        const sno = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
        return (
          <div className="text-[#1F1F1F] font-medium text-center text-borderBlack">
            {String(sno).padStart(2, "0")}
          </div>
        );
      },
    },
    {
      key: "grade",
      title: "Grade",
      dataIndex: "grade",
      minWidth: 148,
      maxWidth: 148,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.type === "QUERY_FEEDBACK"
            ? (record.grade as { name: string }).name
            : (record.assignment?.grade as { name: string }).name}
        </div>
      ),
    },
    {
      key: "type",
      title: "Type",
      minWidth: 200,
      maxWidth: 200,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.type === "QUERY_FEEDBACK"
            ? "Queries"
            : record?.assignment?.assignment_type === "GENERAL"
            ? "General Assignment"
            : "Unit Assignment"}
        </div>
      ),
    },
    {
      key: "teacherName",
      title: "Teacher Name",
      minWidth: 250,
      maxWidth: 250,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record?.teacher?.name}
        </div>
      ),
    },
    {
      key: "lessonName",
      title: "Lesson Name",
      minWidth: 218,
      maxWidth: 218,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.type === "QUERY_FEEDBACK"
            ? (record.lesson as { name: string })?.name
            : (record.assignment?.lesson as { name: string })?.name}
        </div>
      ),
    },
    {
      key: "unitName",
      title: "Unit Name",
      minWidth: 249,
      maxWidth: 249,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.type === "QUERY_FEEDBACK"
            ? (record.unit as { name: string })?.name
            : (record.assignment?.unit as { name: string })?.name}
        </div>
      ),
    },

    {
      key: "status",
      title: "Feedback Status",
      dataIndex: "status",
      minWidth: 204,
      maxWidth: 204,
      sortable: true,
      render: (_, record) => (
        <Badge variant={record.status === "NEW" ? "primary" : "warning"}>
          {capitalizeFirstLetter(record.status)}
        </Badge>
      ),
    },
    {
      key: "date",
      title: "Feedback Date & Time",
      minWidth: 276,
      maxWidth: 276,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.created_date}
        </div>
      ),
    },
  ];

  if (
    hasRolePermissions(userRole?.permissions as string[], [
      ROLE_PERMISSIONS.ACTION_VIEW_FEEDBACK,
    ])
  ) {
    columns.push({
      key: "action",
      title: "Action",
      minWidth: 202,
      maxWidth: 202,
      align: "left",
      render: (_, record) => (
        <div className="flex items-center justify-start space-x-2">
          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_VIEW_FEEDBACK,
            userRole?.permissions as string[]
          ) && (
            <button
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              title="View"
              onClick={() => handleViewFeedback(record)}
            >
              <Eye className="w-6 h-6 text-[#1C1B1F]" />
            </button>
          )}
        </div>
      ),
    });
  }

  const handleViewFeedback = async (record: FeedbackDisplay) => {
    // Navigate to course detail view with assignment tab selected by default

    // Extract the grade ID which we'll use to navigate to the course detail
    const gradeId =
      record.type === "QUERY_FEEDBACK"
        ? (record.grade as { id: string })?.id
        : (record.assignment as { grade: { id: string } })?.grade?.id;

    if (gradeId) {
      dispatch(setNavigationSource("feedback-list"));

      // Navigate to the course detail view with the assignment tab selected
      navigate(`/course-management/view-course/${gradeId}`, {
        state: {
          selectedTab:
            record.type === "QUERY_FEEDBACK" ? "feedback" : "assignment",
          selectedStudentId: record.student.id,
          selectedCardId: record.assignment
            ? record.assignment.id
            : record.query_id,
          assignmentType:
            record?.assignment?.assignment_type === "UNIT" ? "unit" : "general",
          lessonId:
            (record.assignment as { lesson: { id: string } })?.lesson?.id ||
            record.lesson?.id ||
            null,
          unitId:
            (record.assignment as { unit: { id: string } })?.unit?.id ||
            record.unit?.id ||
            null,
        },
      });
    }
  };

  // Handle export of time entries as Excel or PDF
  const handleExport = async (type: "xl" | "pdf" | "csv") => {
    try {
      // Create query params from current filters
      const queryParams: DownloadFeedbackQuery = {
        search: searchValue || undefined,
      };

      const response = await downloadFeedbackService(type, queryParams);

      downloadFileFromBlob(response, type, "feedback_report");

      successToast(
        "Export Successful",
        `Feedbacks exported as ${FileFormats[type].name} successfully`
      );
    } catch (error) {
      console.error(`Error exporting feedbacks as ${type}:`, error);
    }
  };

  // Export options
  const exportOptions: ExportOption[] = [
    {
      label: "PDF",
      icon: <PDFIcon />,
      action: () => handleExport("pdf"),
    },
    {
      label: "Excel",
      icon: <ExcelIcon />,
      action: () => handleExport("xl"),
    },
    {
      label: "CSV",
      icon: <CSVIcon />,
      action: () => handleExport("csv"),
    },
  ];

  if (noFeedbacks) {
    return (
      <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden">
        <div
          className={`flex flex-col h-[calc(100vh-140px)] mb-2 overflow-x-auto justify-center items-center`}
        >
          {loading ? (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Loading...
              </p>
            </>
          ) : (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-bold mb-[9px]">
                No feedback yet
              </p>
              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Your assignment and query feedback will be displayed here.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden  h-full flex flex-1 flex-col">
      {/* List Header with Search, Date Filter, and Export */}
      <ListHeader
        title="Feedbacks List"
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        showDateFilter={false}
        rightAction={
          <>
            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_EXPORT_FEEDBACK,
              userRole?.permissions as string[]
            ) && <ExportDropdown options={exportOptions} />}
          </>
        }
      />

      {/* Table Container with fixed header */}
      <div className="flex flex-1 flex-col border border-[#EDEDED] rounded-md overflow-x-auto">
        <div className="flex-1 overflow-auto h-full flex">
          <Table
            columns={columns}
            data={feedbacks} // Use data from Redux store
            rowKey="id"
            showHeader={true} // Hide header as we're showing it separately above
            size="middle"
            sticky
            scroll={{ x: 1200 }}
            loading={loading} // We're handling loading state ourselves
            // className="body-only-table"
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </div>
      </div>

      {/* Pagination */}

      <ListPagination
        currentPage={currentPage}
        setCurrentPage={handlePageChange}
        totalItems={totalItems}
        itemsPerPage={ITEMS_PER_PAGE}
        itemsLabel="Schools Logs"
      />
    </div>
  );
};

export default FeedbackListSection;
